import React, {MouseEventHandler, useContext, useEffect, useRef, useState} from 'react';
import {GeneralSalesProduct} from '../../../../util/types';
import BolProduct from '../../../../models/bolProductModel';
import {AppContext} from '../../../misc/appContext';
import {ResponseData} from '@dvrd/dvr-controls';
import ProductCostsV2 from '../../../../models/productCostsV2Model';
import {createErrorMessage} from '../../../../util/utils';
import EditProductCostsController from '../productCosts/edit/editProductCostsController';
import ProductVATController from '../../../settings/vat/product/productVATController';
import {ActivateHandle} from '../../../../util/interfaces';
import EditSalesProduct from './editSalesProduct';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    onEditSuccess: VoidFunction;
    onClose: MouseEventHandler;
    product: GeneralSalesProduct | null;
    accountIDs: Set<string>;
}

export default function EditSalesProductController(props: Props) {
    const {account} = useContext(AppContext).accountContext;
    const {t} = useTranslation();
    const {onEditSuccess, product, accountIDs} = props;
    const [bolProduct, setBolProduct] = useState<BolProduct | null>(null);
    const [productCostsList, setProductCostsList] = useState<Array<ProductCostsV2>>([]);
    const [loadingBolProduct, setLoadingBolProduct] = useState(false);
    const [loadingCosts, setLoadingCosts] = useState(false);
    const vatRef = useRef<ActivateHandle>(null);
    const editCostsRef = useRef<ActivateHandle>(null);

    function onClose(evt: React.MouseEvent) {
        props.onClose(evt);
        window.setTimeout(() => {
            setProductCostsList([]);
            setBolProduct(null);
            setLoadingBolProduct(false);
            setLoadingCosts(false);
        }, 200);
    }

    function onEditCosts(costs: ProductCostsV2) {
        return function () {
            editCostsRef.current?.activate(costs);
        };
    }

    function onAddCosts() {
        if (!account || !product) return;
        editCostsRef.current?.activate(new ProductCostsV2({account_id: account.id, ean: product.ean}));
    }

    function onProductCostsSuccess() {
        loadProductCosts();
        onEditSuccess();
    }

    function onEditVAT() {
        if (!bolProduct) return;
        if (product) {
            bolProduct.imageURL = product.image_url;
        }
        vatRef.current?.activate(bolProduct);
    }

    function onVatSuccess(bolProduct: BolProduct) {
        setBolProduct(bolProduct);
        onEditSuccess();
    }

    function onDeleteCosts(costs: ProductCostsV2) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            // Check if costs is an existing record, new records don't have an ID yet
            if (!costs.id.length) return;
            showDialog(
                t(
                    'salesDashboardV2.productDetails.edit.confirmDeleteCosts',
                    'Weet je zeker dat je deze productkosten wilt verwijderen?'
                ),
                t('salesDashboardV2.productDetails.edit.delete', 'Verwijderen'),
                [
                    t('salesDashboardV2.productDetails.edit.no', 'Nee'),
                    {
                        label: t('salesDashboardV2.productDetails.edit.yes', 'Ja'),
                        primary: false,
                        onClick: onConfirmDeleteCosts(costs)
                    }
                ]
            );
        };
    }

    function onConfirmDeleteCosts(costs: ProductCostsV2) {
        return function () {
            setLoadingCosts(true);
            costs.delete((_: ProductCostsV2, success: boolean, data: ResponseData) => {
                setLoadingCosts(false);
                if (success) {
                    showSnackbar(
                        t('salesDashboardV2.productDetails.edit.costsDeleted', 'De productkosten zijn verwijderd')
                    );
                    onProductCostsSuccess();
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'salesDashboardV2.productDetails.edit.errorDeleteCosts',
                                    'Het verwijderen van de productkosten is niet gelukt.'
                                )
                        ),
                        t('salesDashboardV2.productDetails.edit.deleteFailed', 'Verwijderen mislukt')
                    );
            });
        };
    }

    function onChangeVisibility() {
        if (!bolProduct) return;
        setLoadingBolProduct(true);
        const product = bolProduct.clone();
        product.hiddenByUser = !product.hiddenByUser;
        product.update(false, (bolProduct: BolProduct, success: boolean, data: ResponseData) => {
            setLoadingBolProduct(false);
            if (success) {
                setBolProduct(bolProduct);
                onEditSuccess();
            } else
                showDialog(
                    createErrorMessage(
                        data.message ??
                            t(
                                'salesDashboardV2.productDetails.edit.errorChangeVisibility',
                                'Het wijzigen van de zichtbaarheid is niet gelukt.'
                            )
                    ),
                    t('salesDashboardV2.productDetails.edit.changeFailed', 'Wijzigen mislukt')
                );
        });
    }

    async function loadBolProduct() {
        await new Promise<void>(resolve => {
            if (!account || !product) return resolve();
            setLoadingBolProduct(true);
            BolProduct.getByEAN(account.id, product.ean, (bolProduct: BolProduct | null, success: boolean) => {
                setLoadingBolProduct(false);
                if (success) setBolProduct(bolProduct);
                else
                    showDialog(
                        t(
                            'salesDashboardV2.productDetails.edit.productNotFoundInAccount',
                            'Dit product kan niet gevonden worden in het actieve verkoopaccount.'
                        ),
                        t('salesDashboardV2.productDetails.edit.productNotFound', 'Product niet gevonden')
                    );
                resolve();
            });
        });
    }

    async function loadProductCosts() {
        await new Promise<void>(resolve => {
            if (!product) return resolve();
            setLoadingCosts(true);
            ProductCostsV2.getAll(
                Array.from(accountIDs),
                product.ean,
                (productCosts: Array<ProductCostsV2>, success: boolean, data: ResponseData) => {
                    setLoadingCosts(false);
                    if (success) setProductCostsList(productCosts);
                    else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'salesDashboardV2.productDetails.edit.errorLoadCosts',
                                        'Het ophalen van de productkosten is niet gelukt.'
                                    )
                            ),
                            t('salesDashboardV2.productDetails.edit.loadFailed', 'Laden mislukt')
                        );
                    resolve();
                }
            );
        });
    }

    useEffect(() => {
        setLoadingBolProduct(true);
        Promise.allSettled([loadProductCosts(), loadBolProduct()]);
    }, [product, account?.id]);

    return (
        <>
            <EditSalesProduct
                onClose={onClose}
                onEditCosts={onEditCosts}
                onDeleteCosts={onDeleteCosts}
                onAddCosts={onAddCosts}
                onEditVAT={onEditVAT}
                onChangeVisibility={onChangeVisibility}
                productCostsList={productCostsList}
                loadingCosts={loadingCosts}
                loadingProduct={loadingBolProduct}
                bolProduct={bolProduct}
            />
            <EditProductCostsController
                onSuccess={onProductCostsSuccess}
                onDeleteCosts={onDeleteCosts}
                ref={editCostsRef}
            />
            <ProductVATController onSuccess={onVatSuccess} ref={vatRef} />
        </>
    );
}
